import React from 'react';
import { Calendar, Mail, Phone, Globe, MapPin, Instagram, Facebook, Twitter, Star, Award, Users, TrendingUp } from 'lucide-react';

const ProfileCard = ({ 
  websiteData = {
   
  },
  renderExpertiseTags,
  renderSocialLinks,
  renderBookingButton,
  renderContactInfo 
}) => {
  
  const contactItems = [
    { icon: Mail, href: `mailto:${websiteData.email}`, color: "from-blue-400 to-blue-600" },
    { icon: Phone, href: `tel:${websiteData.phone}`, color: "from-green-400 to-green-600" },
    { icon: Globe, href: websiteData.website, color: "from-purple-400 to-purple-600" }
  ];

  return (
    <div className="w-full py-4 relative z-20 mb-8 md:mb-0 -mt-32">
      <div className="w-full">
        <div className="flex flex-col items-center bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-[1.02] relative group w-full p-6 md:p-8 border border-purple-100">
          
          {/* Header Section with Avatar */}
          <div className="relative p-6 pb-0">
            <div className="absolute inset-0 bg-pattern opacity-5"></div>
            
            {/* Avatar */}
            <div className="relative flex justify-center -mb-20 z-10">
              <div className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden group">
                
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-600 rounded-full p-1">
                  <div className="w-full h-full rounded-full overflow-hidden bg-white p-1">
                    <img 
                      src={websiteData.profileImage} 
                      alt={websiteData.name} 
                      className="w-full h-full object-cover rounded-full transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="px-6 pt-24 pb-6">
            
            {/* Name and Title */}
            <div className="text-center mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-gray-800">{websiteData.name}</h1>
              <p className="text-purple-600 font-semibold text-base sm:text-lg mb-4">{websiteData.profession}</p>
              
              {/* Expertise Tags */}
              {renderExpertiseTags()}
            </div>
            
            {/* Bio */}
            <div className="mb-6">
              <p className="text-gray-600 text-center text-sm sm:text-base leading-relaxed">{websiteData.bio}</p>
            </div>
            
            {/* Stats Grid */}
            <div className="mb-6">
              <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800 rounded-2xl p-4 sm:p-6 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-pattern opacity-10"></div>
                <div className="relative z-10">
                  <h3 className="text-lg sm:text-xl font-bold text-center mb-4 sm:mb-6">Proven Results</h3>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    {websiteData.stats?.map((stat, index) => (
                      <div key={index} className="text-center transform hover:scale-105 transition-transform duration-300">
                        <div className="text-xl sm:text-2xl font-bold mb-1 bg-gradient-to-r from-yellow-300 to-yellow-400 bg-clip-text text-transparent">
                          {stat.number}
                        </div>
                        <div className="text-purple-100 text-xs sm:text-sm font-medium leading-tight">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Booking Button */}
            <div className="w-full mb-6">
              <a 
                href={websiteData.calendarBookingPage}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-purple-800 text-white font-bold rounded-xl inline-flex items-center justify-center hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] group-hover:from-purple-700 group-hover:to-purple-900"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book a Consultation
              </a>
            </div>
            
            {/* Contact Information - No Labels, Icons Only */}
            <div className="mb-6">
              <div className="flex justify-center gap-4">
                {contactItems.map((item, index) => (
                  <a 
                    key={index}
                    href={item.href}
                    target={item.href.startsWith('http') ? '_blank' : undefined}
                    rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="group p-3 rounded-full hover:bg-purple-25 transition-all duration-300 cursor-pointer"
                  >
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-md`}>
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                  </a>
                ))}
              </div>
            </div>
            
            {/* Social Links */}
            <div className="text-center mb-6">
              {renderSocialLinks(websiteData.socialLinks)}
            </div>
            
            {/* Location at Bottom Middle */}
            <div className="flex justify-center">
              <div className="flex items-center text-gray-600 group cursor-pointer">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center mr-2 group-hover:scale-110 transition-all duration-300 shadow-md">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <span className="font-medium text-sm">{websiteData.location}</span>
              </div>
            </div>
            
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style>{`
        .bg-pattern {
          background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        }

        .bg-purple-25 {
          background-color: rgb(250, 248, 255);
        }

        .shadow-3xl {
          box-shadow: 0 35px 60px -12px rgba(0, 0, 0, 0.25);
        }

        @media (max-width: 640px) {
          .truncate {
            max-width: 120px;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfileCard;